import { FrontPageImage, FrontPageImageType } from '@/models/common/front-page-image';
import { Link } from '@/models/common/link';
import { PublicationStatusInfo } from '@/models/common/publication-status-info';
import { UserEntityPermissionsType } from '@/models/users/user-permissions/user-entity-permissions';
import { Guid } from 'guid-typescript';

export class AssociationFrontPageSummary {
    public associationId: number = 0;
    public publicationStatus: PublicationStatusInfo[] = [];
    public hasTemporaryUnappliedChanges: boolean = false;
    public images: FrontPageImage[] = [];
    public hasClaim: boolean = false;
    public hasSubclaim: boolean = false;
    public hasDescription: boolean = false;
    public hasContactName: boolean = false;
    public hasContactPhone: boolean = false;
    public hasContactEmail: boolean = false;
    public hasSlug: boolean = false;
    public hasContactAddress: boolean = false;
    public socialMediaLinks: Link[] = [];
    public imagesCount: number = 0;

    public get permissionsGroupType(): UserEntityPermissionsType {
        return UserEntityPermissionsType.Associations;
    }

    public get permissionsIdToCheck(): number {
        return this.associationId;
    }

    get logoUrl(): string | undefined {
        return this.images.find((image) => image.type === FrontPageImageType.Logo)!.url;
    }

    get firstImageUrl(): string | undefined {
        return this.images.find((image) => image.type === FrontPageImageType.FirstImage)!.url;
    }

    get imagesCounter(): string {
        return `${this.imagesCount} / 3`;
    }

    get hasImages(): boolean {
        return this.imagesCount > 0;
    }

    get hasAtLeastOneFrontPagePublished(): boolean {
        return this.publicationStatus.some((p) => p.isPublished);
    }

    constructor(json?: any) {
        if (!json) {
            this.initImages();
            this.initSocialMediaLinks();
            return;
        }

        Object.assign(
            this,
            json,
            { publicationStatus: json.publicationStatus ? json.publicationStatus.map((status: any) => new PublicationStatusInfo(status)) : [] },
            { images: json.images ? json.images.map((image: any) => new FrontPageImage(image)) : [] },
            { socialMediaLinks: json.socialMediaLinks ? json.socialMediaLinks.map((link: any) => new Link(link)) : [] },
            { imagesCount: json.images ? json.images.map((image: any) => new FrontPageImage(image)).filter((image: FrontPageImage) => image.type !== FrontPageImageType.Logo).length : 0 });

        this.initImages();
        this.initSocialMediaLinks();
    }

    private initImages(): void {
        const imagesContext = require.context('@/assets/placeholders/association/', false, /(\.jpg|\.png)$/);
        const imagesContextShared = require.context('@/assets/placeholders/shared/', false, /(\.jpg|\.png)$/);

        if (!this.images.some((image) => image.type === FrontPageImageType.Logo)) {
            this.images.push(new FrontPageImage({ type: FrontPageImageType.Logo, url: imagesContext('./whiteBrandLogoDark.png') }));
        }

        if (!this.images.some((image) => image.type === FrontPageImageType.FirstImage)) {
            this.images.push(new FrontPageImage({ type: FrontPageImageType.FirstImage, url: imagesContextShared('./placeholder16x9.png') }));
        }
    }

    private initSocialMediaLinks(): void {
        this.socialMediaLinks.forEach((link: any) => {
            link.id = Guid.create().toString();
        });
    }
}
